import React from "react"

interface PortfolioCompany {
  id: string
  display_name: string
  logo_url: string | null
  realized: boolean
  liquidated: boolean
  new_this_quarter: boolean
  gics_sector: string | null
  gics_subsector: string | null
  geographic_region: string | null
}

interface SectorGroup {
  sectorName: string
  companies: PortfolioCompany[]
}

interface LayoutItem {
  type: "header" | "sector_content"
  sectorName: string
  companies?: PortfolioCompany[]
}

interface Column {
  items: LayoutItem[]
  itemCount: number
}

const MAX_ITEMS_PER_COLUMN = 8
const MIN_COLUMNS = 3
const MAX_COLUMNS = 4

const useProcessedCompanyData = (
  portfolioCompanies: PortfolioCompany[],
  itemsPerColumn: number = 6,
) => {
  const groupedBySector = portfolioCompanies.reduce(
    (acc, company) => {
      const sector = company.gics_sector || "Uncategorized"
      if (!acc[sector]) {
        acc[sector] = []
      }
      acc[sector].push(company)
      return acc
    },
    {} as Record<string, PortfolioCompany[]>,
  )

  const sortedSectorNames = Object.keys(groupedBySector).sort()

  const sectorGroups: SectorGroup[] = sortedSectorNames.map((sectorName) => ({
    sectorName,
    companies: groupedBySector[sectorName].sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    ),
  }))

  const buildFlatItemList = (sectors: SectorGroup[]): LayoutItem[] => {
    const items: LayoutItem[] = []

    sectors.forEach((sector) => {
      items.push({
        type: "header",
        sectorName: sector.sectorName,
      })

      let i = 0
      while (i < sector.companies.length) {
        const companiesInRow = sector.companies.slice(i, i + 2)
        items.push({
          type: "sector_content",
          sectorName: sector.sectorName,
          companies: companiesInRow,
        })
        i += 2
      }
    })

    return items
  }

  const distributeIntoColumns = (
    items: LayoutItem[],
    itemsPerColumn: number,
  ): Column[] => {
    const columns: Column[] = []

    for (let i = 0; i < items.length; i += itemsPerColumn) {
      const columnItems = items.slice(i, i + itemsPerColumn)
      columns.push({
        items: columnItems,
        itemCount: columnItems.length,
      })
    }

    return columns
  }

  const flatItems = buildFlatItemList(sectorGroups)

  // Scale from MIN_COLUMNS→MAX_COLUMNS to keep items per column ≤ MAX_ITEMS_PER_COLUMN
  const columnsNeededForPageFit = Math.max(
    MIN_COLUMNS,
    Math.ceil(flatItems.length / MAX_ITEMS_PER_COLUMN),
  )
  const effectiveMaxColumns = Math.min(columnsNeededForPageFit, MAX_COLUMNS)

  let finalItemsPerColumn = itemsPerColumn
  let columns = distributeIntoColumns(flatItems, finalItemsPerColumn)

  if (columns.length > effectiveMaxColumns) {
    finalItemsPerColumn = Math.ceil(flatItems.length / effectiveMaxColumns)
    columns = distributeIntoColumns(flatItems, finalItemsPerColumn)
  }

  // Hard cap: truncate if data still exceeds page height
  if (finalItemsPerColumn > MAX_ITEMS_PER_COLUMN) {
    finalItemsPerColumn = MAX_ITEMS_PER_COLUMN
    const maxItems = MAX_ITEMS_PER_COLUMN * MAX_COLUMNS
    columns = distributeIntoColumns(
      flatItems.slice(0, maxItems),
      finalItemsPerColumn,
    )
  }

  const sizeTier =
    finalItemsPerColumn <= 6
      ? "large"
      : finalItemsPerColumn <= 12
      ? "medium"
      : "small"

  return { columns, sizeTier }
}

export const PortfolioCompanyLogos = ({
  portfolioCompanies,
}: {
  portfolioCompanies: PortfolioCompany[]
}) => {
  const { columns, sizeTier } = useProcessedCompanyData(portfolioCompanies)

  const roundedCount =
    Math.round(portfolioCompanies.length / 10) * 10 || portfolioCompanies.length

  return (
    <div
      className={`logos-company-counts size-${sizeTier} cols-${columns.length}`}
    >
      {portfolioCompanies.length === 0 ? (
        <p className="empty-state">
          No portfolio companies found for this period.
        </p>
      ) : (
        <div className="logos-grid-wrapper">
          <div className="company-count-column">
            <div className="company-count-box">
              <div className="count-number">{roundedCount}+</div>
              <div className="count-label">
                underlying
                <br />
                companies
              </div>
            </div>
          </div>
          <div className="logos-grid">
            {columns.map((column, colIdx) => (
              <div key={colIdx} className="logos-column">
                {column.items.map((item, itemIdx) => {
                  if (item.type === "header") {
                    return (
                      <div
                        key={`${colIdx}-${itemIdx}`}
                        className="sector-header-block"
                      >
                        {item.sectorName}
                      </div>
                    )
                  }

                  const companies = item.companies || []
                  return (
                    <div
                      key={`${colIdx}-${itemIdx}`}
                      className="sector-content"
                    >
                      {companies.map((company, companyIdx) => {
                        const isLastAndOdd =
                          companies.length % 2 === 1 &&
                          companyIdx === companies.length - 1

                        const borderClass = company.new_this_quarter
                          ? "new-quarter"
                          : company.realized || company.liquidated
                          ? "realized"
                          : ""

                        return (
                          <div
                            key={company.id}
                            className={`logo-item ${borderClass} ${
                              isLastAndOdd ? "full-width" : ""
                            } ${!company.logo_url ? "no-logo" : ""}`}
                          >
                            {company.logo_url ? (
                              <img
                                src={company.logo_url}
                                alt={`${company.display_name} logo`}
                                onError={(e) => {
                                  const target = e.currentTarget
                                  target.style.display = "none"
                                  const parent = target.parentElement
                                  if (parent) {
                                    parent.classList.add("no-logo")
                                    const span = document.createElement("span")
                                    span.textContent = company.display_name
                                    parent.appendChild(span)
                                  }
                                }}
                              />
                            ) : (
                              <span>{company.display_name}</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const EmptyLogos = () => {
  return (
    <div className="editorjs-empty-tool-table">
      No portfolio companies available
    </div>
  )
}

const hasValidLogosData = (data: any) => {
  return data && Array.isArray(data) && data.length > 0
}

export class PortfolioCompanyLogosTool {
  api: any
  block: any
  createPortal: any
  removePortal: any
  portal: any
  portfolioCompanies: PortfolioCompany[]
  headerInserted: boolean

  constructor(toolConfig: any) {
    this.block = toolConfig.block
    this.api = toolConfig.api
    this.createPortal = toolConfig.config?.createPortal
    this.removePortal = toolConfig.config?.removePortal
    this.portfolioCompanies = toolConfig.config?.portfolioCompanies || []

    this.headerInserted = toolConfig.data?.header_inserted || false

    return this
  }

  static get toolbox() {
    return {
      title: "Portfolio Company Logos",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
    }
  }

  render() {
    const rootEl = document.createElement("div")

    const hasData = hasValidLogosData(this.portfolioCompanies)

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    this.portal = this.createPortal(
      this.block.id,
      hasData ? (
        <PortfolioCompanyLogos portfolioCompanies={this.portfolioCompanies} />
      ) : (
        <EmptyLogos />
      ),
      rootEl,
    )

    return rootEl
  }

  async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Portfolio Companies" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current logos block
      this.api.blocks.insert(
        "header",
        {
          text: "Portfolio Companies",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the logos block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  remove() {
    const totalBlocks = this.api.blocks.getBlocksCount()
    for (let i = 0; i < totalBlocks; i++) {
      const block = this.api.blocks.getBlockByIndex(i)
      if (block && block.id === this.block.id) {
        this.api.blocks.delete(i)
        break
      }
    }
  }

  removed() {
    this.removePortal(this.block.id)
  }

  save() {
    return {
      type: "portfolio_company_logos",
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}
